/**
 * Modal de configuração de gatilhos do flow
 * Permite definir quando o flow será executado e em quais inboxes
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { X, Plus, MessageSquare, Tag, Columns, Building2, UserCheck, Filter, Info, Webhook, Briefcase, FileSignature, Calendar, Clock, Zap, Hash, MessageCircle, Pencil, Trash2, Inbox as InboxIcon, ToggleLeft, ToggleRight, ListChecks, Megaphone, Route } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { TriggerConfig, WebhookTriggerConfig } from '@/types/flows';
import { CrmFilterTriggerConfig } from './CrmFilterTriggerConfig';
// v2.1.0 (2026-05-31): FlowWebhookTriggerConfig removido daqui — configuração de webhook
// agora vive exclusivamente em FlowWebhookDetailDialog (modal "Webhook de Integração").

/**
 * Tipos de gatilhos disponíveis para configuração de fluxos
 *
 * RELEASE: 1.1.0 - 2024-12-26 - Adicionado 'any_message'
 * RELEASE: 1.2.0 - 2026-01-25 - Adicionado 'crm_filter' (filtro avançado CRM)
 * RELEASE: 1.4.0 - 2026-05-31 - Adicionados tipos VIRTUAIS de UI 'crm_filter_deal',
 *                              'crm_filter_conversation' e 'crm_filter_task'. Esses tipos
 *                              existem apenas na UI para distribuir o "Filtro Avançado CRM"
 *                              em cada categoria do menu lateral. Na persistência continuam
 *                              sendo gravados como 'crm_filter' com config.entity_type adequado.
 */
/** RELEASE: 2.0.0 - 2026-08-30 - Removido 'workflow_stage_entered' (Workflow v1 descontinuado) */
type PersistedTriggerType = 'keyword' | 'first_message' | 'scheduled' | 'inactivity' | 'event' | 'webhook' | 'contact_tag' | 'phase' | 'department' | 'active_conversation' | 'any_message' | 'crm_filter' | 'contract_event' | 'campaign_action';

/** Virtuais de UI: representam crm_filter já tipado por entidade na sidebar */
type VirtualCrmFilterType = 'crm_filter_deal' | 'crm_filter_conversation' | 'crm_filter_task';

/** Tipo usado APENAS no estado local do modal (selectedType). Nunca persistido. */
type TriggerType = PersistedTriggerType | VirtualCrmFilterType;

/** Mapeamento virtual → entity_type real (persistido em config.entity_type) */
const VIRTUAL_TO_ENTITY: Record<VirtualCrmFilterType, 'deal' | 'conversation' | 'task'> = {
  crm_filter_deal: 'deal',
  crm_filter_conversation: 'conversation',
  crm_filter_task: 'task',
};

/** Inverso: entity_type → virtual (usado ao abrir um trigger existente para edição) */
const ENTITY_TO_VIRTUAL: Record<'deal' | 'conversation' | 'task', VirtualCrmFilterType> = {
  deal: 'crm_filter_deal',
  conversation: 'crm_filter_conversation',
  task: 'crm_filter_task',
};

/** Helper: identifica se o selectedType é um virtual do filtro CRM */
const isCrmFilterVirtual = (t: TriggerType): t is VirtualCrmFilterType =>
  t === 'crm_filter_deal' || t === 'crm_filter_conversation' || t === 'crm_filter_task';

/** Helper: normaliza qualquer selectedType para a chave realmente persistida */
const toPersistedType = (t: TriggerType): PersistedTriggerType =>
  isCrmFilterVirtual(t) ? 'crm_filter' : t;

interface ConfigureTriggersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggers: TriggerConfig[];
  onSave: (triggers: TriggerConfig[]) => void;
  /**
   * Callback opcional para abrir o modal "Webhook de Integração" (FlowWebhookDetailDialog).
   * Quando informado, gatilhos do tipo `webhook` deixam de ser editáveis inline neste
   * diálogo e passam a redirecionar o usuário para a tela canônica de Webhook do fluxo.
   * Mantém-se assim uma única superfície de configuração de webhook, evitando duplicidade
   * com a aba "Webhook" do hub de Automações.
   */
  onOpenWebhookDetail?: () => void;
}

// Schema de validação para keyword trigger
const keywordSchema = z.object({
  keywords: z.array(z.string().min(1)),
  match_type: z.enum(['exact', 'contains', 'starts_with']),
  case_sensitive: z.boolean(),
});

// Schema de validação para scheduled trigger
const scheduledSchema = z.object({
  cron_expression: z.string().min(1, 'Expressão cron é obrigatória'),
  timezone: z.string(),
});

// Schema de validação para inactivity trigger
const inactivitySchema = z.object({
  minutes: z.number().min(1, 'Mínimo 1 minuto'),
});

// Schema de validação para event trigger
// RELEASE: 1.1.0 - 2024-12-31 - Adicionado 'conversation_reopened'
const eventSchema = z.object({
  event_type: z.enum(['conversation_closed', 'tag_added', 'conversation_transferred', 'conversation_reopened']),
});

// Schema de validação para webhook trigger
const webhookSchema = z.object({
  url: z.string().url('URL inválida'),
  secret: z.string().optional(),
});

/**
 * Helper para garantir que triggers seja sempre um array válido
 * Trata casos onde trigger_config pode ser {} (objeto vazio) ao invés de []
 */
const normalizeTriggers = (input: unknown): TriggerConfig[] => {
  if (Array.isArray(input)) return input;
  return [];
};

export function ConfigureTriggersDialog({
  open,
  onOpenChange,
  triggers,
  onSave,
  onOpenWebhookDetail,
}: ConfigureTriggersDialogProps) {
  const { currentTenant } = useAuth();
  // Garantir que localTriggers sempre seja um array válido usando helper
  const [localTriggers, setLocalTriggers] = useState<TriggerConfig[]>(() => normalizeTriggers(triggers));
  const [selectedType, setSelectedType] = useState<TriggerType>('keyword');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Sincroniza localTriggers e reseta formulário quando o dialog abre
  useEffect(() => {
    if (open) {
      // Usar normalizeTriggers para garantir array válido (trata {} como [])
      setLocalTriggers(normalizeTriggers(triggers));
      // Resetar formulário para evitar dados de flow anterior
      setSelectedType('keyword');
      setEditingIndex(null);
      setKeywords([]);
      setNewKeyword('');
      setMatchType('contains');
      setCaseSensitive(false);
      setCronExpression('');
      setTimezone('America/Sao_Paulo');
      setInactivityMinutes(30);
      setEventType('conversation_closed');
      setWebhookConfig({
        type: 'webhook',
        mode: 'conversation',
        field_mapping: { contact_phone: 'telefone', contact_name: 'nome' },
      });
      setAllInboxes(true);
      setSelectedInboxIds([]);
      // Reset contract_event config
      setContractEventType('contract_signed_partial');
      setContractSignerRoles([]);
      // Reset campaign_action config
      setCampaignAllowAll(true);
      setCampaignAllowedIds([]);
      setCampaignMinStatus('sent');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, triggers]);

  // Estado para seleção de inboxes
  const [allInboxes, setAllInboxes] = useState(true);
  const [selectedInboxIds, setSelectedInboxIds] = useState<string[]>([]);

  // Estado para keyword config
  const [keywords, setKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [matchType, setMatchType] = useState<'exact' | 'contains' | 'starts_with'>('contains');
  const [caseSensitive, setCaseSensitive] = useState(false);

  // Estado para scheduled config
  const [cronExpression, setCronExpression] = useState('');
  const [timezone, setTimezone] = useState('America/Sao_Paulo');

  // Estado para inactivity config
  const [inactivityMinutes, setInactivityMinutes] = useState(30);

  // Estado para event config
  // RELEASE: 1.1.0 - 2024-12-31 - Adicionado 'conversation_reopened'
  const [eventType, setEventType] = useState<'conversation_closed' | 'tag_added' | 'conversation_transferred' | 'conversation_reopened'>('conversation_closed');

  // Estado para webhook config integrado (v2.0.0)
  const [webhookConfig, setWebhookConfig] = useState<WebhookTriggerConfig>({
    type: 'webhook',
    mode: 'conversation',
    field_mapping: {
      contact_phone: 'telefone',
      contact_name: 'nome',
    },
  });

  // Estado para contact_tag config
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [tagCondition, setTagCondition] = useState<'has_tag' | 'tag_added' | 'tag_removed'>('has_tag');

  // Estado para phase config
  const [selectedPhaseIds, setSelectedPhaseIds] = useState<string[]>([]);
  const [phaseCondition, setPhaseCondition] = useState<'entered' | 'is_in'>('entered');
  // Filtro de categoria do bloco "Fase do Kanban" (UI apenas; não persiste)
  // 'all' = exibe todas as fases agrupadas por categoria
  const [phaseCategoryFilter, setPhaseCategoryFilter] = useState<string>('all');

  // Estado para department config
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [departmentCondition, setDepartmentCondition] = useState<'assigned' | 'is_assigned'>('assigned');

  // Estado para active_conversation config
  const [hoursWindow, setHoursWindow] = useState<number>(24);
  // NOVO: Origens a ignorar no gatilho active_conversation
  const [ignoreOrigins, setIgnoreOrigins] = useState<string[]>([]);
  
  // Estado para crm_filter config (Filtro Avançado CRM)
  const [crmFilterConfig, setCrmFilterConfig] = useState<Record<string, any>>({
    entity_type: 'deal',
    time_in_status: 1,
    time_unit: 'days',
    status: 'open',
  });

  // Estado para contract_event config (Eventos de Contrato)
  // RELEASE: 1.3.0 - 2026-02-18 - Novo gatilho de evento de contrato
  const [contractEventType, setContractEventType] = useState<string>('contract_signed_partial');
  const [contractSignerRoles, setContractSignerRoles] = useState<string[]>([]);

  // Estado para campaign_action config
  // RELEASE: 1.0.0 - 2026-06-05 - Novo gatilho público "Campanha"
  // - allowAllCampaigns: quando true (padrão), qualquer campanha pode chamar o fluxo
  // - allowedCampaignIds: whitelist explícita (somente quando allowAllCampaigns=false)
  // - minDispatchStatus: 'sent' só executa em disparos bem-sucedidos; 'any' executa sempre
  const [campaignAllowAll, setCampaignAllowAll] = useState<boolean>(true);
  const [campaignAllowedIds, setCampaignAllowedIds] = useState<string[]>([]);
  const [campaignMinStatus, setCampaignMinStatus] = useState<'sent' | 'any'>('sent');

  const { data: inboxes } = useQuery({
    queryKey: ['inboxes', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, identifier, channel_type')
        .eq('tenant_id', currentTenant!.id)
        .eq('status', 'active')
        .order('name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentTenant,
  });

  // Buscar tags do tenant
  const { data: tags } = useQuery({
    queryKey: ['contact-tags', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contact_tags')
        .select('id, name, color')
        .eq('tenant_id', currentTenant!.id)
        .order('name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentTenant,
  });

  // Buscar fases do kanban
  const { data: phases } = useQuery({
    queryKey: ['kanban-phases', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('kanban_phases')
        .select('id, name, color, category_id')
        .eq('tenant_id', currentTenant!.id)
        .order('position');
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentTenant,
  });

  // Buscar categorias globais para agrupar/filtrar as fases do kanban
  const { data: phaseCategories } = useQuery({
    queryKey: ['crm-workspace-categories-for-phase-trigger'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_workspace_categories')
        .select('id, name, sort_order')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data;
    },
  });


  // Buscar departamentos ativos
  const { data: departments } = useQuery({
    queryKey: ['departments', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('departments')
        .select('id, name')
        .eq('tenant_id', currentTenant!.id)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentTenant,
  });

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !keywords.includes(newKeyword.trim())) {
      setKeywords([...keywords, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setKeywords(keywords.filter((k) => k !== keyword));
  };

  const handleAddTrigger = () => {
    /**
     * NORMALIZAÇÃO DE VIRTUAIS (v1.4.0):
     * Se selectedType for um virtual (crm_filter_deal/conversation/task), ele é
     * convertido para o tipo realmente persistido ('crm_filter') e o entity_type
     * é forçado a partir do virtual escolhido — garantindo coerência mesmo se o
     * estado interno de crmFilterConfig estiver fora de sincronia.
     */
    const persistedType: PersistedTriggerType = toPersistedType(selectedType);
    const lockedEntity = isCrmFilterVirtual(selectedType)
      ? VIRTUAL_TO_ENTITY[selectedType]
      : null;

    /**
     * VALIDAÇÃO DE INBOX CONDICIONAL
     * - Gatilhos CRM com entity_type='deal' NÃO precisam de inbox (deals existem independente de inboxes)
     * - Gatilhos CRM com entity_type='task' NÃO precisam de inbox (tarefas existem independente de inboxes) — v1.3.0
     * - Gatilhos CRM com entity_type='conversation' PRECISAM de inbox (conversas pertencem a inboxes)
     * - Gatilho contract_event NÃO precisa de inbox (contratos existem independente de inboxes)
     * - Todos os outros gatilhos PRECISAM de inbox
     */
    const effectiveEntity = lockedEntity ?? crmFilterConfig.entity_type;
    const isInboxFree =
      persistedType === 'contract_event' ||
      persistedType === 'webhook' ||
      persistedType === 'campaign_action' ||
      (persistedType === 'crm_filter' && (effectiveEntity === 'deal' || effectiveEntity === 'task'));
    const needsInbox = !isInboxFree;


    if (needsInbox && !allInboxes && selectedInboxIds.length === 0) {
      alert('Selecione pelo menos uma inbox ou marque "Todas as inboxes"');
      return;
    }

    let config: any = {};

    switch (persistedType) {
      case 'keyword':
        if (keywords.length === 0) {
          alert('Adicione pelo menos uma palavra-chave');
          return;
        }
        config = { keywords, match_type: matchType, case_sensitive: caseSensitive };
        break;
      case 'first_message':
        config = {};
        break;
      case 'scheduled':
        if (!cronExpression) {
          alert('Expressão cron é obrigatória');
          return;
        }
        config = { cron_expression: cronExpression, timezone };
        break;
      case 'inactivity':
        config = { minutes: inactivityMinutes };
        break;
      case 'event':
        config = { event_type: eventType };
        break;
      case 'webhook':
        // v2.2.0 (2026-06-03): Configuração detalhada (URL, token, mapeamento de
        // campos, destino inbox/pipeline) é feita exclusivamente em
        // "Webhook de Integração" (FlowWebhookDetailDialog). Aqui apenas
        // registramos o gatilho do tipo webhook — sem exigir inbox/phone.
        config = { ...webhookConfig };
        break;
      case 'contact_tag':
        if (selectedTagIds.length === 0) {
          alert('Selecione pelo menos uma tag');
          return;
        }
        config = { tag_ids: selectedTagIds, condition: tagCondition };
        break;
      case 'phase':
        if (selectedPhaseIds.length === 0) {
          alert('Selecione pelo menos uma fase');
          return;
        }
        config = { phase_ids: selectedPhaseIds, condition: phaseCondition };
        break;
      case 'department':
        if (selectedDepartmentIds.length === 0) {
          alert('Selecione pelo menos um departamento');
          return;
        }
        config = { department_ids: selectedDepartmentIds, condition: departmentCondition };
        break;
      case 'active_conversation':
        config = {
          hours_window: hoursWindow,
          // NOVO: Incluir origens a ignorar se houver alguma selecionada
          ignore_origins: ignoreOrigins.length > 0 ? ignoreOrigins : undefined
        };
        break;
      case 'any_message':
        // Fallback universal - sem configuração adicional, mas com prioridade baixa
        config = {};
        break;
      case 'crm_filter':
        // Validar configuração mínima
        if (!crmFilterConfig.time_in_status || crmFilterConfig.time_in_status < 1) {
          alert('Configure o tempo mínimo no status atual');
          return;
        }
        // Mescla config e FORÇA entity_type a partir do virtual quando presente
        config = { ...crmFilterConfig, entity_type: lockedEntity ?? crmFilterConfig.entity_type };
        break;
      case 'contract_event':
        // Evento de contrato não precisa de inbox - opera sobre contratos/contatos do CRM
        config = {
          contract_event_type: contractEventType,
          signer_roles: contractSignerRoles.length > 0 ? contractSignerRoles : undefined,
        };
        break;
      case 'campaign_action':
        // Gatilho público de Campanha (não usa inbox).
        // - allow_all_campaigns: quando true qualquer campanha do tenant pode chamar
        // - allowed_campaign_ids: whitelist apenas quando allow_all_campaigns=false
        // - min_dispatch_status: 'sent' = só dispara em sucesso, 'any' = sempre
        config = {
          allow_all_campaigns: campaignAllowAll,
          allowed_campaign_ids: !campaignAllowAll ? campaignAllowedIds : [],
          min_dispatch_status: campaignMinStatus,
        };
        break;
    }

    // Para any_message, forçar prioridade baixa (999)
    const effectivePriority = persistedType === 'any_message' ? 999 : localTriggers.length;

    /**
     * DEFINIÇÃO DE inbox_ids CONDICIONAL
     * - Para crm_filter com entity_type='deal'|'task': sempre null
     * - Para outros casos: seguir lógica padrão
     */
    let inboxIds = allInboxes ? null : selectedInboxIds;
    if (persistedType === 'crm_filter' && (effectiveEntity === 'deal' || effectiveEntity === 'task')) {
      inboxIds = null;
    }
    if (persistedType === 'contract_event') {
      inboxIds = null;
    }
    if (persistedType === 'webhook') {
      // v2.2.0: webhook não usa inbox alvo (destino é definido em "Webhook de Integração")
      inboxIds = null;
    }
    if (persistedType === 'campaign_action') {
      // Gatilho "Campanha" não usa inbox alvo — restrição por inbox já está em RPC de validação
      inboxIds = null;
    }

    const newTrigger: TriggerConfig = {
      type: persistedType,
      config,
      is_active: true,
      priority: effectivePriority,
      inbox_ids: inboxIds,
    };


    // Usar normalizeTriggers para garantir array válido
    const currentTriggers = normalizeTriggers(localTriggers);
    
    if (editingIndex !== null) {
      const updated = [...currentTriggers];
      updated[editingIndex] = { ...newTrigger, priority: updated[editingIndex].priority };
      setLocalTriggers(updated);
      setEditingIndex(null);
    } else {
      setLocalTriggers([...currentTriggers, newTrigger]);
    }

    // Reset form
    resetForm();
  };

  const resetForm = () => {
    setKeywords([]);
    setNewKeyword('');
    setMatchType('contains');
    setCaseSensitive(false);
    setCronExpression('');
    setTimezone('America/Sao_Paulo');
    setInactivityMinutes(30);
    setEventType('conversation_closed');
    // Reset webhook config integrado
    setWebhookConfig({
      type: 'webhook',
      mode: 'conversation',
      field_mapping: {
        contact_phone: 'telefone',
        contact_name: 'nome',
      },
    });
    setAllInboxes(true);
    setSelectedInboxIds([]);
    // Reset novos gatilhos
    setSelectedTagIds([]);
    setTagCondition('has_tag');
    setSelectedPhaseIds([]);
    setPhaseCondition('entered');
    setSelectedDepartmentIds([]);
    setDepartmentCondition('assigned');
    setHoursWindow(24);
    // NOVO: Reset ignore_origins
    setIgnoreOrigins([]);
    // Reset crm_filter config
    setCrmFilterConfig({
      entity_type: 'deal',

      time_in_status: 1,
      time_unit: 'days',
      status: 'open',
    });
    // Reset campaign_action config
    setCampaignAllowAll(true);
    setCampaignAllowedIds([]);
    setCampaignMinStatus('sent');
  };

  const handleEditTrigger = (index: number) => {
    const trigger = localTriggers[index];
    // Para crm_filter, mapeia para o virtual correspondente para abrir a entrada
    // certa na sidebar (Negócios / Conversas / Tarefas).
    if (trigger.type === 'crm_filter') {
      const ent = (trigger.config?.entity_type as 'deal' | 'conversation' | 'task' | undefined) ?? 'deal';
      setSelectedType(ENTITY_TO_VIRTUAL[ent]);
    } else {
      setSelectedType(trigger.type);
    }
    setEditingIndex(index);

    // Configurar seleção de inboxes
    setAllInboxes(trigger.inbox_ids === null);
    setSelectedInboxIds(trigger.inbox_ids || []);


    switch (trigger.type) {
      case 'keyword':
        setKeywords(trigger.config.keywords || []);
        setMatchType(trigger.config.match_type || 'contains');
        setCaseSensitive(trigger.config.case_sensitive || false);
        break;
      case 'scheduled':
        setCronExpression(trigger.config.cron_expression || '');
        setTimezone(trigger.config.timezone || 'America/Sao_Paulo');
        break;
      case 'inactivity':
        setInactivityMinutes(trigger.config.minutes || 30);
        break;
      case 'event':
        setEventType(trigger.config.event_type || 'conversation_closed');
        break;
      case 'webhook':
        // Carregar configuração do webhook integrado
        setWebhookConfig({
          type: 'webhook',
          mode: trigger.config.mode || 'conversation',
          field_mapping: trigger.config.field_mapping || {
            contact_phone: 'telefone',
            contact_name: 'nome',
          },
          variable_mapping: trigger.config.variable_mapping,
          inbox_id: trigger.config.inbox_id,
          inbox_name: trigger.config.inbox_name,
          workspace_id: trigger.config.workspace_id,
          workspace_name: trigger.config.workspace_name,
          pipeline_id: trigger.config.pipeline_id,
          pipeline_name: trigger.config.pipeline_name,
          phase_id: trigger.config.phase_id,
          phase_name: trigger.config.phase_name,
          default_source: trigger.config.default_source,
          seller_id: trigger.config.seller_id,
          seller_name: trigger.config.seller_name,
          require_auth: trigger.config.require_auth,
          auth_header_name: trigger.config.auth_header_name,
          auth_header_value: trigger.config.auth_header_value,
        });
        break;
      case 'contact_tag':
        setSelectedTagIds(trigger.config.tag_ids || []);
        setTagCondition(trigger.config.condition || 'has_tag');
        break;
      case 'phase':
        setSelectedPhaseIds(trigger.config.phase_ids || []);
        setPhaseCondition(trigger.config.condition || 'entered');
        break;
      case 'department':
        setSelectedDepartmentIds(trigger.config.department_ids || []);
        setDepartmentCondition(trigger.config.condition || 'assigned');
        break;
      case 'active_conversation':
        setHoursWindow(trigger.config.hours_window || 24);
        // NOVO: Carregar origens a ignorar
        setIgnoreOrigins(trigger.config.ignore_origins || []);
        break;
      case 'crm_filter':
        setCrmFilterConfig(trigger.config || {
          entity_type: 'deal',
          time_in_status: 1,
          time_unit: 'days',
          status: 'open',
        });
        break;
      case 'contract_event':
        // Carrega configuração de evento de contrato ao editar
        setContractEventType(trigger.config.contract_event_type || 'contract_signed_partial');
        setContractSignerRoles(trigger.config.signer_roles || []);
        break;
      case 'campaign_action':
        // Carrega configuração do gatilho de Campanha ao editar
        setCampaignAllowAll(trigger.config.allow_all_campaigns !== false);
        setCampaignAllowedIds(trigger.config.allowed_campaign_ids || []);
        setCampaignMinStatus(trigger.config.min_dispatch_status === 'any' ? 'any' : 'sent');
        break;
    }
  };

  const handleRemoveTrigger = (index: number) => {
    setLocalTriggers(localTriggers.filter((_, i) => i !== index));
  };

  const handleToggleTrigger = (index: number) => {
    const updated = [...localTriggers];
    updated[index].is_active = !updated[index].is_active;
    setLocalTriggers(updated);
  };

  const handleSave = () => {
    onSave(localTriggers);
    onOpenChange(false);
  };

  /**
   * Wrapper para troca de tipo na sidebar / select mobile.
   * Quando o usuário escolhe um virtual (crm_filter_<x>), forçamos imediatamente
   * o entity_type correto em crmFilterConfig, garantindo que o formulário
   * carregue os filtros pertinentes à entidade — sem depender do seletor antigo.
   */
  const handleSelectType = (type: TriggerType) => {
    if (isCrmFilterVirtual(type)) {
      const entity = VIRTUAL_TO_ENTITY[type];
      setCrmFilterConfig((prev) => {
        // Se já estamos editando outro entity_type, preservar campos básicos de tempo/status
        const base = prev?.entity_type === entity ? prev : {
          time_in_status: prev?.time_in_status ?? 1,
          time_unit: prev?.time_unit ?? 'days',
          status: prev?.status ?? 'open',
        };
        return { ...base, entity_type: entity };
      });
    }
    setSelectedType(type);
  };


  const getTriggerLabel = (type: PersistedTriggerType) => {
    const labels: Record<PersistedTriggerType, string> = {
      keyword: 'Palavra-chave',
      first_message: 'Primeira mensagem',
      scheduled: 'Agendado',
      inactivity: 'Inatividade',
      event: 'Evento',
      webhook: 'Webhook',
      contact_tag: 'Tag do contato',
      phase: 'Fase',
      department: 'Departamento',
      active_conversation: 'Conversa em andamento',
      any_message: 'Qualquer mensagem',
      crm_filter: 'Filtro Avançado CRM',
      contract_event: 'Evento de Contrato',
      campaign_action: 'Campanha',
    };
    return labels[type];
  };

  /**
   * Retorna descrição amigável do gatilho baseada em seu tipo e configuração
   */
  const getTriggerDescription = (trigger: TriggerConfig) => {
    switch (trigger.type) {
      case 'keyword':
        return `Palavras: ${trigger.config.keywords?.join(', ')}`;
      case 'first_message':
        return 'Primeira mensagem do contato';
      case 'scheduled':
        return `Cron: ${trigger.config.cron_expression}`;
      case 'inactivity':
        return `${trigger.config.minutes} minutos de inatividade`;
      case 'event': {
        // RELEASE: 1.1.0 - 2024-12-31 - Adicionado 'conversation_reopened'
        const eventTypeLabels: Record<string, string> = {
          conversation_closed: 'Conversa fechada',
          tag_added: 'Tag adicionada',
          conversation_transferred: 'Conversa transferida',
          conversation_reopened: 'Conversa reaberta',
        };
        return `Evento: ${eventTypeLabels[trigger.config.event_type] || trigger.config.event_type}`;
      }
      case 'webhook': {
        // RELEASE: 2.0.0 - 2026-01-30 - Webhook integrado usa mode/destination ao invés de url
        const webhookConfig = trigger as unknown as WebhookTriggerConfig;
        
        // Verificar se é nova estrutura ou legado (para compatibilidade)
        if (webhookConfig.mode) {
          const modeLabel = webhookConfig.mode === 'conversation' 
            ? 'Conversa' 
            : 'Negócios';
          const destination = webhookConfig.mode === 'conversation'
            ? webhookConfig.inbox_name || 'Inbox não configurada'
            : webhookConfig.pipeline_name || 'Pipeline não configurado';
          return `${modeLabel}: ${destination}`;
        }
        
        // Fallback para estrutura legada (se existir)
        const legacyConfig = trigger.config as any;
        return legacyConfig?.url || 'Webhook não configurado';
      }
      case 'contact_tag': {
        const conditionLabels = { has_tag: 'Possui', tag_added: 'Adicionada', tag_removed: 'Removida' };
        const tagNames = trigger.config.tag_ids?.map((id: string) => 
          tags?.find(t => t.id === id)?.name
        ).filter(Boolean).join(', ') || 'Nenhuma';
        return `${conditionLabels[trigger.config.condition as keyof typeof conditionLabels] || 'Tag'}: ${tagNames}`;
      }
      case 'phase': {
        const phaseConditionLabels = { entered: 'Entrou em', is_in: 'Está em' };
        const phaseNames = trigger.config.phase_ids?.map((id: string) => 
          phases?.find(p => p.id === id)?.name
        ).filter(Boolean).join(', ') || 'Nenhuma';
        return `${phaseConditionLabels[trigger.config.condition as keyof typeof phaseConditionLabels] || 'Fase'}: ${phaseNames}`;
      }
      case 'department': {
        const deptConditionLabels = { assigned: 'Atribuído a', is_assigned: 'Está em' };
        const deptNames = trigger.config.department_ids?.map((id: string) => 
          departments?.find(d => d.id === id)?.name
        ).filter(Boolean).join(', ') || 'Nenhum';
        return `${deptConditionLabels[trigger.config.condition as keyof typeof deptConditionLabels] || 'Dept'}: ${deptNames}`;
      }
      case 'active_conversation': {
        const hours = trigger.config.hours_window || 24;
        // NOVO: Mostrar origens ignoradas na descrição
        const ignored = trigger.config.ignore_origins || [];
        const originLabels: Record<string, string> = { campaign: 'Campanha', bot: 'Bot', agent: 'Agente' };
        const ignoredText = ignored.length > 0 
          ? ` | Ignora: ${ignored.map((o: string) => originLabels[o] || o).join(', ')}`
          : '';
        const hoursText = hours > 0 
          ? `Última msg há menos de ${hours}h` 
          : 'Qualquer conversa ativa';
        return hoursText + ignoredText;
      }
      case 'any_message':
        return 'Fallback: qualquer mensagem recebida';
      case 'campaign_action': {
        // Resumo do gatilho de Campanha
        const allowAll = trigger.config?.allow_all_campaigns !== false;
        const minStatus = trigger.config?.min_dispatch_status === 'any' ? 'qualquer status' : 'apenas sucesso';
        const wl = (trigger.config?.allowed_campaign_ids || []) as string[];
        const scope = allowAll ? 'Todas as campanhas' : `${wl.length} campanha(s) específicas`;
        return `${scope} · ${minStatus}`;
      }
      case 'contract_event': {
        // Labels legíveis por tipo de evento de contrato
        const contractEventLabels: Record<string, string> = {
          contract_sent: 'Enviado para assinatura',
          contract_signed_partial: 'Signatário assinou',
          contract_signed_all: 'Todos assinaram',
          contract_refused: 'Signatário recusou',
          contract_expired: 'Contrato expirou',
          contract_cancelled: 'Contrato cancelado',
          contract_viewed: 'Signatário visualizou',
        };
        const evtLabel = contractEventLabels[trigger.config.contract_event_type] || trigger.config.contract_event_type;
        const roles = trigger.config.signer_roles || [];
        return roles.length > 0 ? `${evtLabel} (${roles.join(', ')})` : evtLabel;
      }
      case 'crm_filter': {
        const timeValue = trigger.config.time_in_status || 1;
        const timeUnit = trigger.config.time_unit || 'days';
        const unitLabels: Record<string, string> = { 
          minutes: 'min', hours: 'h', days: 'd', weeks: 'sem' 
        };
        const entityLabel = trigger.config.entity_type === 'conversation' ? 'Conversa' : 'Negócio';
        const phaseText = trigger.config.phase_names?.length 
          ? ` em ${trigger.config.phase_names.join(', ')}`
          : '';
        return `${entityLabel}${phaseText} há >${timeValue}${unitLabels[timeUnit]}`;
      }
      default:
        return '';
    }
  };

  // ========================================================================
  // METADADOS VISUAIS DOS TIPOS DE GATILHO (Minimal Airy redesign)
  // - Centralizamos ícone + label + descrição curta usados na sidebar e no
  //   cabeçalho da área principal, sem alterar nenhuma lógica existente.
  // ========================================================================
  const TRIGGER_META: Record<TriggerType, { label: string; icon: any; desc: string }> = {
    crm_filter:               { label: 'Filtro Avançado CRM',     icon: Filter,        desc: 'Dispara com base em entidade, fase e tempo no status' },
    // VIRTUAIS de UI (persistem como crm_filter + entity_type)
    crm_filter_deal:          { label: 'Filtro Avançado — Negócios',  icon: Briefcase, desc: 'Dispara para negócios que atendem aos filtros e ao tempo mínimo no status' },
    crm_filter_conversation:  { label: 'Filtro Avançado — Conversas', icon: MessageCircle, desc: 'Dispara para conversas que atendem aos filtros e ao tempo mínimo no status' },
    crm_filter_task:          { label: 'Filtro Avançado — Tarefas',   icon: ListChecks, desc: 'Dispara para tarefas que atendem aos filtros e ao tempo mínimo no status' },
    first_message:       { label: 'Primeira mensagem',   icon: Zap,           desc: 'Aciona quando o contato envia a primeira mensagem' },
    any_message:         { label: 'Qualquer mensagem',   icon: MessageCircle, desc: 'Fallback para qualquer mensagem recebida' },
    keyword:             { label: 'Palavra-chave',       icon: Hash,          desc: 'Aciona ao detectar palavras específicas na mensagem' },
    inactivity:          { label: 'Inatividade',         icon: Clock,         desc: 'Aciona após X minutos sem resposta do contato' },
    event:               { label: 'Evento',              icon: Info,          desc: 'Eventos do ciclo de vida da conversa' },
    contact_tag:         { label: 'Tag do contato',      icon: Tag,           desc: 'Quando uma tag é adicionada, removida ou está presente' },
    phase:               { label: 'Fase do Kanban',      icon: Columns,       desc: 'Quando o contato entra ou está em uma fase' },
    department:          { label: 'Departamento',        icon: Building2,     desc: 'Quando a conversa é atribuída a um departamento' },
    active_conversation: { label: 'Conversa em andamento', icon: UserCheck,   desc: 'Conversas com interação recente dentro de uma janela' },
    contract_event:      { label: 'Evento de Contrato',  icon: FileSignature, desc: 'Eventos do ciclo de vida de contratos digitais' },
    scheduled:           { label: 'Agendado (cron)',     icon: Calendar,      desc: 'Execução programada via expressão cron' },
    webhook:             { label: 'Webhook',             icon: Webhook,       desc: 'Recebe payloads externos para iniciar o flow' },
    campaign_action:     { label: 'Campanha',            icon: Megaphone,     desc: 'Permite que ações pós-disparo de campanhas chamem este fluxo' },
  };

  // Agrupamento por categoria. Cada categoria mostra SUA versão do Filtro Avançado
  // (deal / conversation / task). O tipo persistido continua sendo 'crm_filter'.
  const TRIGGER_CATEGORIES: { label: string; items: TriggerType[]; placeholder?: string }[] = [
    { label: 'Negócios',  items: ['crm_filter_deal'] },
    { label: 'Conversas', items: ['crm_filter_conversation', 'first_message', 'any_message', 'keyword', 'inactivity', 'event', 'contact_tag', 'phase', 'department', 'active_conversation'] },
    { label: 'Tarefas',   items: ['crm_filter_task'] },
    { label: 'Contratos', items: ['contract_event'] },
    { label: 'Campanhas', items: ['campaign_action'] },
    { label: 'Outros',    items: ['scheduled', 'webhook'] },
  ];

  // Contagem de gatilhos já configurados por tipo (para badges na sidebar).
  // Para virtuais, contamos crm_filter cujo entity_type corresponde ao virtual.
  const countByType = localTriggers.reduce<Record<string, number>>((acc, t) => {
    if (t.type === 'crm_filter') {
      const ent = (t.config?.entity_type as 'deal' | 'conversation' | 'task' | undefined) ?? 'deal';
      const virtualKey = ENTITY_TO_VIRTUAL[ent];
      acc[virtualKey] = (acc[virtualKey] || 0) + 1;
    } else {
      acc[t.type] = (acc[t.type] || 0) + 1;
    }
    return acc;
  }, {});


  const ActiveIcon = TRIGGER_META[selectedType].icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1200px] w-[95vw] h-[85vh] p-0 overflow-hidden flex flex-col gap-0">
        {/* ================================================================
            HEADER COMPACTO (Minimal Airy)
            ================================================================ */}
        <DialogHeader className="px-6 py-4 border-b shrink-0 space-y-1">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <DialogTitle className="text-lg font-semibold tracking-tight">
                Configurar Gatilhos do Flow
              </DialogTitle>
              <DialogDescription className="text-sm">
                Defina quando este flow será executado automaticamente. Se ele for iniciado por um
                processo (Workflow), os gatilhos abaixo não são usados e os dados do registro chegam
                como variáveis do grupo &quot;Processo (Workflow)&quot;.
              </DialogDescription>

            </div>
            <Badge variant="secondary" className="shrink-0 font-medium">
              {localTriggers.length} {localTriggers.length === 1 ? 'gatilho' : 'gatilhos'} configurado{localTriggers.length === 1 ? '' : 's'}
            </Badge>
          </div>
        </DialogHeader>

        {/* ================================================================
            CORPO: 2 COLUNAS (sidebar de categorias + área principal)
            ================================================================ */}
        <div className="flex-1 min-h-0 flex">
          {/* ---------------- SIDEBAR ---------------- */}
          <aside className="w-[280px] shrink-0 border-r bg-muted/20 overflow-y-auto hidden md:block">
            <nav className="p-3 space-y-5">
              {TRIGGER_CATEGORIES.map((cat) => (
                <div key={cat.label} className="space-y-1">
                  <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {cat.label}
                  </div>
                  {cat.items.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground/70 italic">
                      {cat.placeholder}
                    </div>
                  ) : (
                    cat.items.map((type) => {
                      const meta = TRIGGER_META[type];
                      const Icon = meta.icon;
                      const isActive = selectedType === type;
                      const count = countByType[type] || 0;
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => handleSelectType(type)}
                          className={cn(
                            'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-all text-left group',
                            isActive
                              ? 'bg-primary/10 text-primary border-l-2 border-primary font-medium'
                              : 'text-foreground/80 hover:bg-muted hover:text-foreground border-l-2 border-transparent'
                          )}
                        >
                          <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')} />
                          <span className="flex-1 truncate">{meta.label}</span>
                          {count > 0 && (
                            <Badge variant={isActive ? 'default' : 'secondary'} className="h-5 px-1.5 text-[10px] font-semibold">
                              {count}
                            </Badge>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              ))}
            </nav>
          </aside>

          {/* ---------------- MAIN ---------------- */}
          <main className="flex-1 min-w-0 overflow-y-auto">
            <div className="p-6 space-y-6 max-w-5xl mx-auto">

              {/* Fallback mobile: seletor horizontal de categorias quando sidebar oculta */}
              <div className="md:hidden">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tipo de gatilho</Label>
                <Select value={selectedType} onValueChange={(v) => handleSelectType(v as TriggerType)}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIGGER_CATEGORIES.flatMap((cat) =>
                      cat.items.map((type) => (
                        <SelectItem key={type} value={type}>
                          {cat.label} · {TRIGGER_META[type].label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Cabeçalho do tipo selecionado */}
              <div className="flex items-start gap-3 pb-4 border-b">
                <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <ActiveIcon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold tracking-tight">{TRIGGER_META[selectedType].label}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{TRIGGER_META[selectedType].desc}</p>
                </div>
                {editingIndex !== null && (
                  <Badge variant="outline" className="shrink-0">
                    <Pencil className="h-3 w-3 mr-1" /> Editando
                  </Badge>
                )}
              </div>

              {/* ============== FORMULÁRIO DO TIPO SELECIONADO ============== */}
              <div className="space-y-4">

                {/* Alertas informativos */}
                {selectedType === 'crm_filter_deal' && (
                  <div className="flex gap-3 rounded-lg border border-border bg-card px-4 py-3 border-l-[3px] border-l-primary">
                    <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div className="space-y-0.5">
                      <p className="text-sm font-semibold text-foreground">Gatilho CRM</p>
                      <p className="text-sm text-muted-foreground">
                        Este gatilho opera sobre negócios/deals do CRM, que podem existir sem
                        vínculo com inbox de mensagens. A seleção de inbox não se aplica.
                      </p>
                    </div>
                  </div>
                )}

                {selectedType === 'crm_filter_task' && (
                  <div className="flex gap-3 rounded-lg border border-border bg-card px-4 py-3 border-l-[3px] border-l-primary">
                    <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div className="space-y-0.5">
                      <p className="text-sm font-semibold text-foreground">Gatilho Tarefa</p>
                      <p className="text-sm text-muted-foreground">
                        Este gatilho opera sobre tarefas do módulo de Tarefas, que podem existir
                        sem vínculo com inbox ou negócio. A seleção de inbox não se aplica.
                      </p>
                    </div>
                  </div>
                )}

                {selectedType === 'contract_event' && (
                  <div className="flex gap-3 rounded-lg border border-border bg-card px-4 py-3 border-l-[3px] border-l-primary">
                    <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div className="space-y-0.5">
                      <p className="text-sm font-semibold text-foreground">Gatilho de Contrato</p>
                      <p className="text-sm text-muted-foreground">
                        Este gatilho opera sobre eventos do ciclo de vida de contratos digitais,
                        que não possuem vínculo direto com inboxes de mensagens. A seleção de
                        inbox não se aplica.
                      </p>
                    </div>
                  </div>
                )}

                {/* SELEÇÃO DE INBOXES (lógica original preservada — agora considera virtuais).
                    Inbox só aparece quando o tipo NÃO é deal/task do filtro CRM, NÃO é contract_event
                    e NÃO é webhook (webhook tem destino próprio configurado em "Webhook de Integração"). */}
                {selectedType !== 'contract_event' &&
                  selectedType !== 'webhook' &&
                  selectedType !== 'crm_filter_deal' &&
                  selectedType !== 'crm_filter_task' && (
                  <div className="rounded-xl border bg-card p-5 space-y-4">
                    <div className="flex items-center gap-2">
                      <InboxIcon className="h-4 w-4 text-muted-foreground" />
                      <Label className="text-sm font-semibold">Inboxes alvo</Label>
                    </div>
                    {/* Grid 2 colunas: à esquerda controle "Todas", à direita lista de inboxes */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="all-inboxes"
                            checked={allInboxes}
                            onCheckedChange={(checked) => {
                              setAllInboxes(checked as boolean);
                              if (checked) setSelectedInboxIds([]);
                            }}
                          />
                          <Label htmlFor="all-inboxes" className="font-medium cursor-pointer">
                            Todas as inboxes
                          </Label>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Marque para acionar este gatilho em todas as inboxes do tenant. Desmarque para selecionar inboxes específicas ao lado.
                        </p>
                      </div>

                      {!allInboxes ? (
                        <div className="space-y-2">
                          {/* Cabeçalho com label + contador discreto "X de Y selecionadas" */}
                          <div className="flex items-center justify-between gap-2">
                            <Label className="text-xs text-muted-foreground">
                              Selecione as inboxes onde este gatilho será aplicado:
                            </Label>
                            <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
                              {selectedInboxIds.length} de {inboxes?.length ?? 0} selecionadas
                            </span>
                          </div>
                          {/* Lista com rolagem interna + scrollbar fina (.scrollbar-thin definida em src/index.css) */}
                          <div className="space-y-1 max-h-64 overflow-y-auto scrollbar-thin border rounded-lg p-2">

                            {inboxes?.map((inbox) => (
                              <div key={inbox.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`inbox-${inbox.id}`}
                                  checked={selectedInboxIds.includes(inbox.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedInboxIds([...selectedInboxIds, inbox.id]);
                                    } else {
                                      setSelectedInboxIds(selectedInboxIds.filter(id => id !== inbox.id));
                                    }
                                  }}
                                />
                                <Label htmlFor={`inbox-${inbox.id}`} className="font-normal cursor-pointer">
                                  <span className="font-medium">{inbox.name}</span>
                                  <span className="text-xs text-muted-foreground ml-2">
                                    ({inbox.identifier})
                                  </span>
                                </Label>
                              </div>
                            ))}
                          </div>
                          {selectedInboxIds.length === 0 && (
                            <p className="text-xs text-amber-600">
                              ⚠️ Selecione pelo menos uma inbox ou marque "Todas as inboxes"
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center text-xs text-muted-foreground border border-dashed rounded-lg p-4">
                          Todas as inboxes habilitadas
                        </div>
                      )}
                    </div>
                  </div>

                )}

                {/* Wrapper visual unificado para os blocos de form específicos */}
                <div className="rounded-xl border bg-card p-5 space-y-4">

                  {selectedType === 'keyword' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Palavras-chave ocupa linha inteira (chips podem crescer) */}
                      <div className="space-y-2 md:col-span-2">
                        <Label>Palavras-chave</Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="Digite uma palavra-chave"
                            value={newKeyword}
                            onChange={(e) => setNewKeyword(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                          />
                          <Button onClick={handleAddKeyword} size="sm">
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {keywords.map((keyword) => (
                            <Badge key={keyword} variant="secondary">
                              {keyword}
                              <button
                                onClick={() => handleRemoveKeyword(keyword)}
                                className="ml-2 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {/* Tipo de correspondência (col esquerda) */}
                      <div className="space-y-2">
                        <Label>Tipo de correspondência</Label>
                        <Select value={matchType} onValueChange={(v: any) => setMatchType(v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="exact">Exata</SelectItem>
                            <SelectItem value="contains">Contém</SelectItem>
                            <SelectItem value="starts_with">Começa com</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Case sensitive (col direita) */}
                      <div className="space-y-2">
                        <Label>Sensibilidade</Label>
                        <div className="flex items-center gap-2 h-10 px-3 border rounded-md">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setCaseSensitive(!caseSensitive)}
                            title={caseSensitive ? 'Desativar' : 'Ativar'}
                          >
                            {caseSensitive ? (
                              <ToggleRight className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                          <span className="text-sm">Case sensitive (maiúsculas/minúsculas)</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedType === 'first_message' && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/40 border">
                      <MessageSquare className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <p className="text-sm text-muted-foreground">
                        Este gatilho será acionado quando o contato enviar sua <strong>primeira mensagem</strong>.
                        Não há parâmetros adicionais — basta selecionar as inboxes acima.
                      </p>
                    </div>
                  )}

                  {selectedType === 'scheduled' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Expressão Cron</Label>
                        <Input
                          placeholder="0 9 * * 1-5"
                          value={cronExpression}
                          onChange={(e) => setCronExpression(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                          Exemplo: "0 9 * * 1-5" = Todo dia útil às 9h
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Fuso horário</Label>
                        <Select value={timezone} onValueChange={setTimezone}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="America/Sao_Paulo">América/São Paulo (BRT)</SelectItem>
                            <SelectItem value="America/New_York">América/Nova York (EST)</SelectItem>
                            <SelectItem value="Europe/London">Europa/Londres (GMT)</SelectItem>
                            <SelectItem value="UTC">UTC</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {selectedType === 'inactivity' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Minutos de inatividade</Label>
                        <Input
                          type="number"
                          min="1"
                          value={inactivityMinutes}
                          onChange={(e) => setInactivityMinutes(parseInt(e.target.value))}
                        />
                        <p className="text-xs text-muted-foreground">
                          Flow será executado após este tempo sem resposta do contato
                        </p>
                      </div>
                      <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/40 border">
                        <Clock className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <p className="text-sm text-muted-foreground">
                          O sistema verifica periodicamente as conversas e dispara o flow quando o tempo configurado for atingido sem nova interação.
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedType === 'event' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Tipo de evento</Label>
                        <Select value={eventType} onValueChange={(v: any) => setEventType(v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="conversation_closed">Conversa fechada</SelectItem>
                            <SelectItem value="tag_added">Tag adicionada</SelectItem>
                            <SelectItem value="conversation_transferred">Conversa transferida</SelectItem>
                            <SelectItem value="conversation_reopened">Conversa reaberta</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/40 border">
                        <Zap className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <p className="text-sm text-muted-foreground">
                          O gatilho dispara assim que o evento selecionado ocorrer em qualquer conversa das inboxes monitoradas.
                        </p>
                      </div>
                    </div>
                  )}



                  {/*
                   * v2.1.0 (2026-05-31): A configuração de webhook deixou de ser editada
                   * aqui. O usuário agora gerencia o webhook (URL, token, mapeamento,
                   * logs e estatísticas) exclusivamente em "Webhook de Integração"
                   * (FlowWebhookDetailDialog), acessível pelo menu do fluxo ou pela aba
                   * "Webhook" do hub de Automações. Isso elimina a duplicidade de UI.
                   *
                   * v2.2.0 (2026-06-03): Mostramos um aviso visível quando o usuário
                   * escolhe Webhook, explicando o fluxo de 2 passos: (1) Adicionar
                   * Gatilho aqui; (2) configurar URL/token/mapeamento na tela canônica.
                   */}
                  {selectedType === 'webhook' && (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Webhook className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-foreground">
                            Como configurar um gatilho de Webhook
                          </p>
                          <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
                            <li>Clique em <strong>Adicionar Gatilho</strong> abaixo para registrar o gatilho do tipo Webhook neste fluxo.</li>
                            <li>Em seguida, abra <strong>Webhook de Integração</strong> para definir URL, token, mapeamento de campos e destino (inbox ou pipeline).</li>
                          </ol>
                        </div>
                      </div>
                      {onOpenWebhookDetail && (
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              onOpenChange(false);
                              onOpenWebhookDetail();
                            }}
                          >
                            Abrir Webhook de Integração
                          </Button>
                        </div>
                      )}
                    </div>
                  )}


                  {selectedType === 'contact_tag' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Condição</Label>
                        <Select value={tagCondition} onValueChange={(v: any) => setTagCondition(v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="has_tag">Contato possui a tag</SelectItem>
                            <SelectItem value="tag_added">Tag foi adicionada</SelectItem>
                            <SelectItem value="tag_removed">Tag foi removida</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Resumo</Label>
                        <div className="flex items-center h-10 px-3 border rounded-md text-sm text-muted-foreground">
                          {selectedTagIds.length} tag(s) selecionada(s)
                        </div>
                      </div>
                      {/* Lista de tags em grid de chips — ocupa linha inteira */}
                      <div className="space-y-2 md:col-span-2">
                        <Label>Tags</Label>
                        {tags && tags.length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                            {tags.map((tag) => (
                              <div key={tag.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`tag-${tag.id}`}
                                  checked={selectedTagIds.includes(tag.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedTagIds([...selectedTagIds, tag.id]);
                                    } else {
                                      setSelectedTagIds(selectedTagIds.filter(id => id !== tag.id));
                                    }
                                  }}
                                />
                                <Label htmlFor={`tag-${tag.id}`} className="cursor-pointer flex items-center gap-2 min-w-0">
                                  <Badge
                                    variant="secondary"
                                    style={{ backgroundColor: tag.color || undefined }}
                                    className="truncate"
                                  >
                                    {tag.name}
                                  </Badge>
                                </Label>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Nenhuma tag cadastrada</p>
                        )}
                        {selectedTagIds.length === 0 && (
                          <p className="text-xs text-amber-600">⚠️ Selecione pelo menos uma tag</p>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedType === 'phase' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Condição</Label>
                        <Select value={phaseCondition} onValueChange={(v: any) => setPhaseCondition(v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="entered">Contato entrou na fase</SelectItem>
                            <SelectItem value="is_in">Contato está na fase</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Resumo</Label>
                        <div className="flex items-center h-10 px-3 border rounded-md text-sm text-muted-foreground">
                          {selectedPhaseIds.length} fase(s) selecionada(s)
                        </div>
                      </div>
                      {/* Filtro por categoria */}
                      <div className="space-y-2 md:col-span-2">
                        <Label>Categoria</Label>
                        <Select value={phaseCategoryFilter} onValueChange={setPhaseCategoryFilter}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todas as categorias</SelectItem>
                            {(() => {
                              // Apenas categorias que possuem ao menos uma fase neste tenant
                              const usedIds = new Set((phases || []).map(p => p.category_id).filter(Boolean));
                              return (phaseCategories || [])
                                .filter(c => usedIds.has(c.id))
                                .map(c => (
                                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                ));
                            })()}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label>Fases</Label>
                        {phases && phases.length > 0 ? (
                          (() => {
                            // Filtrar pelas categoria selecionada
                            const visiblePhases = phaseCategoryFilter === 'all'
                              ? phases
                              : phases.filter(p => p.category_id === phaseCategoryFilter);

                            if (visiblePhases.length === 0) {
                              return (
                                <div className="border rounded-lg p-3 text-sm text-muted-foreground">
                                  Nenhuma fase nesta categoria
                                </div>
                              );
                            }

                            // Renderiza grid de checkboxes para um conjunto de fases
                            const renderPhaseGrid = (list: typeof phases) => (
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                {list.map((phase) => (
                                  <div key={phase.id} className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`phase-${phase.id}`}
                                      checked={selectedPhaseIds.includes(phase.id)}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setSelectedPhaseIds([...selectedPhaseIds, phase.id]);
                                        } else {
                                          setSelectedPhaseIds(selectedPhaseIds.filter(id => id !== phase.id));
                                        }
                                      }}
                                    />
                                    <Label htmlFor={`phase-${phase.id}`} className="cursor-pointer flex items-center gap-2 min-w-0">
                                      <div
                                        className="w-3 h-3 rounded-full shrink-0"
                                        style={{ backgroundColor: phase.color || '#888' }}
                                      />
                                      <span className="truncate">{phase.name}</span>
                                    </Label>
                                  </div>
                                ))}
                              </div>
                            );

                            // Modo categoria específica: grid simples
                            if (phaseCategoryFilter !== 'all') {
                              return (
                                <div className="border rounded-lg p-3 max-h-64 overflow-y-auto scrollbar-thin">
                                  {renderPhaseGrid(visiblePhases)}
                                </div>
                              );
                            }

                            // Modo "Todas as categorias": agrupar por category_id
                            const catMap = new Map((phaseCategories || []).map(c => [c.id, c]));
                            const groups = new Map<string, typeof phases>();
                            for (const p of visiblePhases) {
                              const key = p.category_id || '__none__';
                              if (!groups.has(key)) groups.set(key, [] as any);
                              groups.get(key)!.push(p);
                            }
                            // Ordena grupos por sort_order da categoria; "sem categoria" por último
                            const orderedKeys = Array.from(groups.keys()).sort((a, b) => {
                              if (a === '__none__') return 1;
                              if (b === '__none__') return -1;
                              return (catMap.get(a)?.sort_order ?? 0) - (catMap.get(b)?.sort_order ?? 0);
                            });

                            return (
                              <div className="border rounded-lg p-3 max-h-64 overflow-y-auto scrollbar-thin space-y-3">
                                {orderedKeys.map((key, idx) => {
                                  const groupPhases = groups.get(key)!;
                                  const catName = key === '__none__'
                                    ? 'Sem categoria'
                                    : (catMap.get(key)?.name || 'Categoria');
                                  const groupIds = groupPhases.map(p => p.id);
                                  const allSelected = groupIds.every(id => selectedPhaseIds.includes(id));
                                  const toggleGroup = () => {
                                    if (allSelected) {
                                      setSelectedPhaseIds(selectedPhaseIds.filter(id => !groupIds.includes(id)));
                                    } else {
                                      const next = new Set(selectedPhaseIds);
                                      groupIds.forEach(id => next.add(id));
                                      setSelectedPhaseIds(Array.from(next));
                                    }
                                  };
                                  return (
                                    <div key={key} className={idx > 0 ? 'pt-3 border-t border-border/40' : ''}>
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                          {catName}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={toggleGroup}
                                          className="text-[11px] text-primary hover:underline"
                                        >
                                          {allSelected ? 'Desmarcar todas' : 'Marcar todas'}
                                        </button>
                                      </div>
                                      {renderPhaseGrid(groupPhases)}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()
                        ) : (
                          <p className="text-sm text-muted-foreground">Nenhuma fase cadastrada</p>
                        )}
                        {selectedPhaseIds.length === 0 && (
                          <p className="text-xs text-amber-600">⚠️ Selecione pelo menos uma fase</p>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedType === 'department' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Condição</Label>
                        <Select value={departmentCondition} onValueChange={(v: any) => setDepartmentCondition(v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="assigned">Conversa foi atribuída ao departamento</SelectItem>
                            <SelectItem value="is_assigned">Conversa está atribuída ao departamento</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Resumo</Label>
                        <div className="flex items-center h-10 px-3 border rounded-md text-sm text-muted-foreground">
                          {selectedDepartmentIds.length} departamento(s) selecionado(s)
                        </div>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Departamentos</Label>
                        {departments && departments.length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                            {departments.map((dept) => (
                              <div key={dept.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`dept-${dept.id}`}
                                  checked={selectedDepartmentIds.includes(dept.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedDepartmentIds([...selectedDepartmentIds, dept.id]);
                                    } else {
                                      setSelectedDepartmentIds(selectedDepartmentIds.filter(id => id !== dept.id));
                                    }
                                  }}
                                />
                                <Label htmlFor={`dept-${dept.id}`} className="cursor-pointer flex items-center gap-2 min-w-0">
                                  <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                                  <span className="truncate">{dept.name}</span>
                                </Label>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Nenhum departamento cadastrado</p>
                        )}
                        {selectedDepartmentIds.length === 0 && (
                          <p className="text-xs text-amber-600">⚠️ Selecione pelo menos um departamento</p>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedType === 'active_conversation' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Janela de tempo (horas)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="720"
                          value={hoursWindow}
                          onChange={(e) => setHoursWindow(parseInt(e.target.value) || 0)}
                          placeholder="24"
                        />
                        <p className="text-xs text-muted-foreground">
                          Se 0: qualquer conversa em andamento dispara o gatilho.
                          Se &gt; 0: dispara apenas se a última interação foi há menos de X horas.
                        </p>
                      </div>
                      <Alert className="bg-primary/5 border-primary/20">
                        <UserCheck className="h-4 w-4 text-primary" />
                        <AlertDescription className="text-foreground/80">
                          Este gatilho dispara quando a última mensagem (do contato ou agente)
                          está dentro da janela de tempo configurada.
                        </AlertDescription>
                      </Alert>

                      {/* Ignorar origens — ocupa linha inteira com grid 3 colunas */}
                      <div className="space-y-2 md:col-span-2 pt-2 border-t">
                        <Label>Ignorar conversas originadas de:</Label>
                        <p className="text-xs text-muted-foreground">
                          O gatilho NÃO será ativado se a última mensagem antes da atual for de uma dessas origens.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border rounded-lg p-3">
                          {[
                            { value: 'campaign', label: 'Campanha', desc: 'Mensagens disparadas por campanhas' },
                            { value: 'bot', label: 'Bot', desc: 'Mensagens enviadas pelo fluxo/bot' },
                            { value: 'agent', label: 'Agente', desc: 'Mensagens enviadas por agentes humanos' },
                          ].map((origin) => (
                            <div key={origin.value} className="flex items-start space-x-3">
                              <Checkbox
                                id={`origin-${origin.value}`}
                                checked={ignoreOrigins.includes(origin.value)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setIgnoreOrigins([...ignoreOrigins, origin.value]);
                                  } else {
                                    setIgnoreOrigins(ignoreOrigins.filter(o => o !== origin.value));
                                  }
                                }}
                                className="mt-0.5"
                              />
                              <div className="min-w-0">
                                <Label htmlFor={`origin-${origin.value}`} className="cursor-pointer font-medium">
                                  {origin.label}
                                </Label>
                                <p className="text-xs text-muted-foreground">{origin.desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedType === 'any_message' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex gap-3 rounded-lg border border-border bg-card px-4 py-3 border-l-[3px] border-l-amber-500">
                        <MessageSquare className="h-4 w-4 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
                        <div className="space-y-0.5">
                          <p className="text-sm font-semibold text-foreground">Gatilho de fallback</p>
                          <p className="text-sm text-muted-foreground">
                            Dispara para qualquer mensagem recebida quando nenhum outro gatilho
                            mais específico corresponder.
                          </p>
                        </div>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">
                          Este gatilho é configurado automaticamente com <strong>prioridade baixa (999)</strong>,
                          garantindo que outros gatilhos sejam avaliados primeiro.
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          💡 Use em conjunto com o node <strong>Verificar Gatilho</strong> para ramificar
                          o fluxo e tratar mensagens não categorizadas na saída "Padrão".
                        </p>
                      </div>
                    </div>
                  )}


                  {isCrmFilterVirtual(selectedType) && (
                    <CrmFilterTriggerConfig
                      config={crmFilterConfig}
                      onChange={setCrmFilterConfig}
                      lockedEntityType={VIRTUAL_TO_ENTITY[selectedType]}
                    />
                  )}


                  {selectedType === 'contract_event' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Evento do contrato</Label>
                        <Select value={contractEventType} onValueChange={setContractEventType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="contract_sent">Contrato enviado para assinatura</SelectItem>
                            <SelectItem value="contract_signed_partial">Signatário assinou</SelectItem>
                            <SelectItem value="contract_signed_all">Todos os signatários assinaram</SelectItem>
                            <SelectItem value="contract_refused">Signatário recusou</SelectItem>
                            <SelectItem value="contract_expired">Contrato expirou</SelectItem>
                            <SelectItem value="contract_cancelled">Contrato cancelado</SelectItem>
                            <SelectItem value="contract_viewed">Signatário visualizou</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Alert>
                        <FileSignature className="h-4 w-4" />
                        <AlertDescription>
                          <strong>Gatilho de Contrato:</strong> Dispara quando um evento do ciclo de vida do contrato ocorrer.
                          Variáveis disponíveis: <code className="bg-muted px-1 rounded text-xs">{'{{contract_id}}'}</code>, <code className="bg-muted px-1 rounded text-xs">{'{{signer_name}}'}</code>, <code className="bg-muted px-1 rounded text-xs">{'{{signer_role}}'}</code>, <code className="bg-muted px-1 rounded text-xs">{'{{signer_email}}'}</code>.
                        </AlertDescription>
                      </Alert>

                      {/* Filtro por papel — grid 4 colunas ocupando linha inteira */}
                      {['contract_signed_partial', 'contract_refused', 'contract_viewed'].includes(contractEventType) && (
                        <div className="space-y-2 md:col-span-2">
                          <Label>Filtrar por papel do signatário</Label>
                          <p className="text-xs text-muted-foreground">
                            Vazio = dispara para qualquer papel. Selecione para filtrar.
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 border rounded-lg p-3">
                            {[
                              { value: 'contratante', label: 'Contratante' },
                              { value: 'contratado', label: 'Contratado' },
                              { value: 'representante', label: 'Representante' },
                              { value: 'testemunha', label: 'Testemunha' },
                            ].map((role) => (
                              <div key={role.value} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`role-${role.value}`}
                                  checked={contractSignerRoles.includes(role.value)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setContractSignerRoles([...contractSignerRoles, role.value]);
                                    } else {
                                      setContractSignerRoles(contractSignerRoles.filter(r => r !== role.value));
                                    }
                                  }}
                                />
                                <Label htmlFor={`role-${role.value}`} className="cursor-pointer">
                                  {role.label}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ============================================================
                       Form do gatilho "Campanha" (campaign_action)
                       RELEASE: 1.0.0 - 2026-06-05
                       - Não precisa de inbox (campanha já é multi-inbox)
                       - allow_all_campaigns: padrão TRUE para evitar fricção
                       - whitelist por campanha aparece apenas quando desligado
                       ============================================================ */}
                  {selectedType === 'campaign_action' && (
                    <div className="space-y-4">
                      <div className="flex gap-3 rounded-lg border border-border bg-card px-4 py-3 border-l-[3px] border-l-primary">
                        <Megaphone className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <div className="space-y-0.5">
                          <p className="text-sm font-semibold text-foreground">Gatilho de Campanha</p>
                          <p className="text-sm text-muted-foreground">
                            Permite que ações pós-disparo de campanhas chamem este fluxo. O fluxo
                            só aparece na lista de seleção dentro da campanha quando este gatilho
                            estiver <strong>ativo</strong>.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Origem permitida */}
                        <div className="space-y-2">
                          <Label>Quais campanhas podem chamar este fluxo?</Label>
                          <Select
                            value={campaignAllowAll ? 'all' : 'whitelist'}
                            onValueChange={(v) => setCampaignAllowAll(v === 'all')}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todas as campanhas do tenant</SelectItem>
                              <SelectItem value="whitelist">Apenas campanhas selecionadas</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Whitelist é útil quando o fluxo é específico de uma campanha.
                          </p>
                        </div>

                        {/* Filtro por status do disparo */}
                        <div className="space-y-2">
                          <Label>Disparar para qual resultado de envio?</Label>
                          <Select
                            value={campaignMinStatus}
                            onValueChange={(v: 'sent' | 'any') => setCampaignMinStatus(v)}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sent">Apenas disparos com sucesso (sent)</SelectItem>
                              <SelectItem value="any">Qualquer resultado (sucesso ou falha)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            "Apenas sucesso" evita acionar o fluxo em contatos sem conversa criada.
                          </p>
                        </div>

                        {/* Lista de IDs (quando whitelist) — formato simples por enquanto */}
                        {!campaignAllowAll && (
                          <div className="space-y-2 md:col-span-2">
                            <Label>IDs de campanhas autorizadas</Label>
                            <Textarea
                              rows={3}
                              placeholder="Cole um UUID por linha (ou separados por vírgula)"
                              value={campaignAllowedIds.join('\n')}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const ids = raw
                                  .split(/[\s,]+/)
                                  .map((s) => s.trim())
                                  .filter((s) => s.length > 0);
                                setCampaignAllowedIds(ids);
                              }}
                            />
                            <p className="text-xs text-muted-foreground">
                              {campaignAllowedIds.length} campanha(s) autorizadas.
                              Dica: copie o ID da campanha na URL ao editá-la.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                </div>


                {/* Ação primária — adicionar/atualizar gatilho */}
                <div className="flex justify-end gap-2 pt-2">
                  {editingIndex !== null && (
                    <Button size="xs" variant="ghost" onClick={() => { setEditingIndex(null); resetForm(); }}>
                      Cancelar edição
                    </Button>
                  )}
                  <Button size="xs" onClick={handleAddTrigger} className="min-w-[180px]">
                    <Plus className="mr-1.5" />
                    {editingIndex !== null ? 'Atualizar Gatilho' : 'Adicionar Gatilho'}
                  </Button>
                </div>
              </div>

              {/* ============== LISTA DE GATILHOS CONFIGURADOS ============== */}
              {localTriggers.length > 0 && (
                <div className="space-y-3 pt-4 border-t">
                  {/*
                   * v2.1.0 (2026-05-31): Aviso de migração — exibido somente quando o
                   * fluxo já possui um gatilho do tipo `webhook`. Direciona o usuário
                   * para a tela canônica "Webhook de Integração", que centraliza
                   * configuração, URL/token, logs e estatísticas em um único lugar.
                   */}
                  {onOpenWebhookDetail && localTriggers.some((t) => t.type === 'webhook') && (
                    <Alert className="border-primary/30 bg-primary/5">
                      <Webhook className="h-4 w-4 text-primary" />
                      <AlertDescription className="flex items-center justify-between gap-3 flex-wrap">
                        <span className="text-sm">
                          Webhooks de Integração agora são gerenciados em <strong>Webhook de Integração</strong> no menu do fluxo.
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            onOpenChange(false);
                            onOpenWebhookDetail();
                          }}
                        >
                          Abrir Webhook de Integração
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Gatilhos configurados</Label>
                    <span className="text-xs text-muted-foreground">{localTriggers.length} no total</span>
                  </div>
                  <div className="space-y-2">
                    {localTriggers.map((trigger, index) => {
                      const meta = TRIGGER_META[trigger.type];
                      const TriggerIcon = meta?.icon || Info;
                      return (
                        <div
                          key={index}
                          className={cn(
                            'flex items-center justify-between p-3 border rounded-lg bg-card hover:border-primary/30 hover:shadow-sm transition-all',
                            editingIndex === index && 'border-primary/50 bg-primary/5'
                          )}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {/* Toggle ativar/desativar — padrão unificado com tela de Departamentos */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleToggleTrigger(index)}
                              title={trigger.is_active ? 'Desativar' : 'Ativar'}
                            >
                              {trigger.is_active ? (
                                <ToggleRight className="h-4 w-4 text-emerald-600" />
                              ) : (
                                <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                            <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                              <TriggerIcon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium">{getTriggerLabel(trigger.type)}</span>
                                <span className="text-sm text-muted-foreground truncate">
                                  · {getTriggerDescription(trigger)}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 mt-1 flex-wrap">
                                {(!trigger.inbox_ids || trigger.inbox_ids === null) ? (
                                  trigger.type === 'webhook' && (trigger as unknown as WebhookTriggerConfig).inbox_name ? (
                                    <Badge variant="secondary" className="text-xs">
                                      <Webhook className="h-3 w-3 mr-1" />
                                      {(trigger as unknown as WebhookTriggerConfig).inbox_name}
                                    </Badge>
                                  ) : trigger.type === 'webhook' && (trigger as unknown as WebhookTriggerConfig).mode === 'deal' ? (
                                    <Badge variant="secondary" className="text-xs">
                                      <Briefcase className="h-3 w-3 mr-1" />
                                      {(trigger as unknown as WebhookTriggerConfig).pipeline_name || 'CRM'}
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary" className="text-xs">
                                      <MessageSquare className="h-3 w-3 mr-1" />
                                      Todas as inboxes
                                    </Badge>
                                  )
                                ) : (
                                  trigger.inbox_ids.map((inboxId) => {
                                    const inbox = inboxes?.find(i => i.id === inboxId);
                                    return inbox ? (
                                      <Badge key={inboxId} variant="secondary" className="text-xs">
                                        <MessageSquare className="h-3 w-3 mr-1" />
                                        {inbox.name}
                                      </Badge>
                                    ) : null;
                                  })
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            {trigger.type === 'webhook' ? (
                              // v2.1.0 (2026-05-31): Webhooks são gerenciados exclusivamente
                              // no modal "Webhook de Integração". O botão abaixo redireciona
                              // o usuário para essa tela canônica em vez de editar inline.
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  if (onOpenWebhookDetail) {
                                    onOpenChange(false);
                                    onOpenWebhookDetail();
                                  }
                                }}
                                title="Abrir Webhook de Integração"
                                disabled={!onOpenWebhookDetail}
                              >
                                <Webhook className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditTrigger(index)}
                                title="Editar"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveTrigger(index)}
                              title="Remover"
                              className="hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>

        {/* ================================================================
            FOOTER FIXO
            ================================================================ */}
        <DialogFooter className="px-6 py-3 border-t shrink-0 bg-muted/20">
          <Button size="xs" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button size="xs" onClick={handleSave}>Salvar Gatilhos</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
